import type { CollisionWorld } from './collision.js';

/**
 * Deterministic player movement. The client runs this for prediction and the server runs the
 * exact same function for authority; snapshots carry the full MoveState so the client can
 * reconcile and replay unacknowledged commands. No trigonometry is used for positions so
 * results are bit-identical across JavaScript engines.
 */
export const MOVE = {
  runSpeed: 5.2,
  sprintSpeed: 7.8,
  dashSpeed: 14,
  dashDuration: 0.26,
  dashStaminaCost: 25,
  dashCooldown: 0.55,
  /** Invulnerability window at the start of a dodge (seconds). */
  dashInvulnerable: 0.22,
  staminaMax: 100,
  sprintDrainPerSec: 22,
  staminaRegenPerSec: 18,
  /** After exhausting stamina, sprinting stays locked until stamina recovers to this value. */
  sprintUnlockAt: 25,
} as const;

export interface MoveState {
  x: number;
  z: number;
  /** Unit facing vector. */
  fx: number;
  fz: number;
  stamina: number;
  /** Remaining dash time (s). */
  dashT: number;
  dashX: number;
  dashZ: number;
  /** Remaining dodge cooldown (s). */
  dashCd: number;
  /** Remaining command ticks during which voluntary movement is suppressed (casting). */
  rootTicks: number;
  sprintLocked: boolean;
  /** Basic-attack combo: index of the next combo step and remaining ticks of the chaining window. */
  comboStep: number;
  comboTicks: number;
  /** True when the last step moved because of input (used for animation and stamina). */
  moving: boolean;
  sprinting: boolean;
}

export interface MoveCommand {
  /** Desired direction; magnitude is clamped to 1. */
  mx: number;
  mz: number;
  sprint: boolean;
  dodge: boolean;
}

export interface MoveContext {
  world: CollisionWorld;
  radius: number;
  /** Multiplier from slows/hastes (1 = normal). */
  speedMul: number;
  /** False while dead or stunned. */
  canAct: boolean;
}

export function createMoveState(x: number, z: number, fx = 0, fz = 1): MoveState {
  return {
    x,
    z,
    fx,
    fz,
    stamina: MOVE.staminaMax,
    dashT: 0,
    dashX: 0,
    dashZ: 0,
    dashCd: 0,
    rootTicks: 0,
    sprintLocked: false,
    comboStep: 0,
    comboTicks: 0,
    moving: false,
    sprinting: false,
  };
}

export function cloneMoveState(s: MoveState): MoveState {
  return { ...s };
}

/** True while the dodge invulnerability window is active. */
export function isDodgeInvulnerable(s: MoveState): boolean {
  return s.dashT > MOVE.dashDuration - MOVE.dashInvulnerable;
}

/** Validates and normalises an untrusted command in place. Returns false if it must be rejected. */
export function sanitizeCommand(cmd: MoveCommand): boolean {
  if (!Number.isFinite(cmd.mx) || !Number.isFinite(cmd.mz)) return false;
  const l2 = cmd.mx * cmd.mx + cmd.mz * cmd.mz;
  if (l2 > 1.0001) {
    const l = Math.sqrt(l2);
    cmd.mx /= l;
    cmd.mz /= l;
  }
  return true;
}

/** Advances `s` in place by one fixed step of `dt` seconds. */
export function stepMovement(s: MoveState, cmd: MoveCommand, ctx: MoveContext, dt: number): void {
  const rooted = s.rootTicks > 0 || !ctx.canAct;
  if (s.rootTicks > 0) s.rootTicks -= 1;
  if (s.comboTicks > 0) {
    s.comboTicks -= 1;
    if (s.comboTicks === 0) s.comboStep = 0;
  }
  if (s.dashCd > 0) s.dashCd = Math.max(0, s.dashCd - dt);

  const inLen = Math.sqrt(cmd.mx * cmd.mx + cmd.mz * cmd.mz);
  const hasInput = inLen > 0.05;

  // Start a dodge.
  if (cmd.dodge && !rooted && s.dashT <= 0 && s.dashCd <= 0 && s.stamina >= MOVE.dashStaminaCost) {
    let dx = s.fx;
    let dz = s.fz;
    if (hasInput) {
      dx = cmd.mx / inLen;
      dz = cmd.mz / inLen;
    }
    s.dashT = MOVE.dashDuration;
    s.dashX = dx;
    s.dashZ = dz;
    s.dashCd = MOVE.dashDuration + MOVE.dashCooldown;
    s.stamina -= MOVE.dashStaminaCost;
  }

  let vx = 0;
  let vz = 0;
  s.sprinting = false;
  if (s.dashT > 0 && ctx.canAct) {
    const t = Math.min(dt, s.dashT);
    vx = s.dashX * MOVE.dashSpeed * (t / dt);
    vz = s.dashZ * MOVE.dashSpeed * (t / dt);
    s.dashT = Math.max(0, s.dashT - dt);
    s.fx = s.dashX;
    s.fz = s.dashZ;
  } else {
    s.dashT = 0;
    if (hasInput && !rooted) {
      const mag = Math.min(1, inLen);
      const nx = cmd.mx / inLen;
      const nz = cmd.mz / inLen;
      const wantsSprint = cmd.sprint && !s.sprintLocked && s.stamina > 0 && mag > 0.6;
      const speed = (wantsSprint ? MOVE.sprintSpeed : MOVE.runSpeed) * mag * ctx.speedMul;
      vx = nx * speed;
      vz = nz * speed;
      s.fx = nx;
      s.fz = nz;
      if (wantsSprint) {
        s.sprinting = true;
        s.stamina = Math.max(0, s.stamina - MOVE.sprintDrainPerSec * dt);
        if (s.stamina <= 0) s.sprintLocked = true;
      }
    }
  }

  if (!s.sprinting && s.dashT <= 0) {
    s.stamina = Math.min(MOVE.staminaMax, s.stamina + MOVE.staminaRegenPerSec * dt);
  }
  if (s.sprintLocked && s.stamina >= MOVE.sprintUnlockAt) s.sprintLocked = false;

  s.moving = vx !== 0 || vz !== 0;
  if (s.moving) {
    const p = ctx.world.resolve(s.x + vx * dt, s.z + vz * dt, ctx.radius);
    s.x = p.x;
    s.z = p.z;
  }
}

/** Number of fixed command ticks covering `ms` milliseconds. */
export function msToTicks(ms: number, hz: number): number {
  return Math.ceil((ms / 1000) * hz - 1e-9);
}
